<template>
  <BaseTemplate app-name="Eventos" page-title="">
    <template #content>
      <div class="min-h-screen bg-slate-100 px-3 py-4 transition-colors duration-300 dark:bg-gray-900 sm:px-4 lg:px-6">
        <div class="mx-auto max-w-6xl space-y-4">

          <!-- ====== NAVEGAÇÃO ====== -->
          <nav class="flex items-center justify-between gap-2" aria-label="Navegação">
            <button type="button" class="btn-ghost -ml-2 text-blue-600 dark:text-blue-400" @click="voltar">
              <ArrowLeft class="h-4 w-4" /> Eventos
            </button>
            <button type="button" class="btn-ghost" :disabled="isRefreshing" aria-label="Recarregar" @click="recarregar">
              <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': isRefreshing }" />
            </button>
          </nav>

          <!-- ====== CARREGANDO / ERRO ====== -->
          <div v-if="isLoading" class="space-y-4" aria-busy="true">
            <div class="card h-32 animate-pulse bg-gray-200/60 dark:bg-gray-700/40"></div>
            <div class="grid gap-4 lg:grid-cols-3">
              <div class="card h-72 animate-pulse bg-gray-200/60 dark:bg-gray-700/40 lg:col-span-2"></div>
              <div class="card h-72 animate-pulse bg-gray-200/60 dark:bg-gray-700/40"></div>
            </div>
          </div>

          <div v-else-if="erro" class="card flex flex-col items-center gap-3 p-10 text-center">
            <XCircle class="h-10 w-10 text-red-500" />
            <p class="font-medium text-gray-700 dark:text-gray-200">{{ erro }}</p>
            <div class="flex gap-2">
              <button type="button" class="btn-secondary" @click="voltar">Voltar para a lista</button>
              <button type="button" class="btn-primary" @click="carregar">Tentar novamente</button>
            </div>
          </div>

          <template v-else-if="evento">
            <!-- ====== CABEÇALHO DO EVENTO ====== -->
            <header class="card relative overflow-hidden p-4 sm:p-6">
              <div class="absolute inset-y-0 left-0 w-1.5" :class="statusMeta(evento.status).dot" aria-hidden="true"></div>
              <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                  <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Evento #{{ evento.id }} · {{ categoriaMeta(evento.categoria_evento).longo }}
                  </p>
                  <h1 class="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{{ evento.unidade?.nome }}</h1>
                  <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    <template v-if="evento.equipamento">
                      {{ evento.equipamento.nome }}<span v-if="evento.equipamento.modelo" class="text-gray-400"> · {{ evento.equipamento.modelo }}</span>
                      <span class="badge ml-2 align-middle" :class="equipamentoMeta(evento.equipamento.status).badge">{{ equipamentoMeta(evento.equipamento.status).label }}</span>
                    </template>
                    <span v-else class="text-gray-400">Sem equipamento vinculado</span>
                  </p>
                </div>
                <div class="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  <div class="flex flex-wrap gap-1">
                    <span class="badge px-3 py-1 text-sm" :class="statusMeta(evento.status).badge">
                      <span class="h-2 w-2 rounded-full" :class="statusMeta(evento.status).dot"></span>
                      {{ statusMeta(evento.status).label }}
                    </span>
                    <span v-if="evento.atrasado" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Atrasado</span>
                    <span v-if="evento.is_parada && evento.status === 'em_atendimento'" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Equipamento parado</span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ resumoTempo }}</p>
                </div>
              </div>

              <!-- Ações -->
              <div v-if="podeGerir" class="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                <button v-if="evento.status === 'pendente'" type="button" class="btn-primary" :disabled="acaoEmAndamento" @click="confirmar('iniciar')">
                  <Play class="h-4 w-4" /> Iniciar agora
                </button>
                <button v-if="evento.status !== 'finalizado'" type="button" class="btn bg-emerald-600 text-white hover:bg-emerald-700" :disabled="acaoEmAndamento" @click="abrirFinalizar">
                  <CheckCircle2 class="h-4 w-4" /> Finalizar
                </button>
                <button v-if="evento.status !== 'finalizado'" type="button" class="btn-secondary" :disabled="acaoEmAndamento" @click="abrirEdicao">
                  <Pencil class="h-4 w-4" /> Editar
                </button>
                <button v-else type="button" class="btn-secondary" :disabled="acaoEmAndamento" @click="confirmar('reabrir')">
                  <RotateCcw class="h-4 w-4" /> Reabrir
                </button>
                <button type="button" class="btn-secondary" :disabled="acaoEmAndamento" :title="evento.orcamento ? 'Atualizar orçamento' : 'Informar orçamento'" @click="abrirOrcamento">
                  <Wallet class="h-4 w-4" /> {{ evento.orcamento ? 'Orçamento' : 'Informar orçamento' }}
                </button>
                <button type="button" class="btn-ghost ml-auto text-red-600 dark:text-red-400" :disabled="acaoEmAndamento" @click="confirmar('excluir')">
                  <Trash2 class="h-4 w-4" /> Excluir
                </button>
              </div>
              <p v-else-if="evento.status === 'finalizado'" class="mt-4 text-xs text-gray-500 dark:text-gray-400">
                Evento finalizado em {{ formatarDataHora(evento.data_fechamento) }}.
              </p>
            </header>

            <div class="grid gap-4 lg:grid-cols-3">
              <!-- ====== COLUNA PRINCIPAL ====== -->
              <div class="space-y-4 lg:col-span-2">
                <section class="card p-4 sm:p-6">
                  <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-100">Descrição</h2>
                  <p class="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">{{ evento.descricao }}</p>
                </section>

                <section class="card p-4 sm:p-6">
                  <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-100">Informações</h2>
                  <dl class="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                    <div v-for="item in informacoes" :key="item.label" :class="item.classe">
                      <dt class="text-xs text-gray-500 dark:text-gray-400">{{ item.label }}</dt>
                      <dd class="mt-0.5 font-medium text-gray-900 dark:text-gray-100">
                        <span v-if="item.badge" class="badge" :class="item.badge">{{ item.valor }}</span>
                        <template v-else>{{ item.valor }}</template>
                      </dd>
                    </div>
                  </dl>
                </section>

                <section class="card p-4 sm:p-6">
                  <h2 class="text-sm font-semibold text-gray-800 dark:text-gray-100">Linha do tempo</h2>
                  <ol class="mt-3 space-y-4">
                    <li v-for="(etapa, i) in linhaDoTempo" :key="etapa.label" class="relative flex gap-3 pl-1">
                      <span v-if="i < linhaDoTempo.length - 1" class="absolute left-[0.8rem] top-6 h-full w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
                      <span class="relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" :class="etapa.concluida ? etapa.cor : 'bg-gray-200 dark:bg-gray-700'">
                        <component :is="etapa.icon" class="h-3 w-3" :class="etapa.concluida ? 'text-white' : 'text-gray-500 dark:text-gray-300'" />
                      </span>
                      <div class="min-w-0">
                        <p class="text-sm font-medium" :class="etapa.concluida ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'">{{ etapa.label }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ etapa.descricao }}</p>
                      </div>
                    </li>
                  </ol>
                </section>
              </div>

              <!-- ====== COMENTÁRIOS ====== -->
              <aside class="card flex flex-col p-4 sm:p-6 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start">
                <h2 class="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                  <MessageSquare class="h-4 w-4" /> Comentários
                  <span class="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{{ comentarios.length }}</span>
                </h2>

                <div ref="listaComentarios" class="mt-3 flex-1 space-y-3 overflow-y-auto pr-1" :class="comentarios.length ? 'max-h-[28rem]' : ''">
                  <p v-if="!comentarios.length" class="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                    Nenhum comentário ainda. Registre aqui o andamento do atendimento.
                  </p>
                  <article v-for="c in comentarios" :key="c.id" class="rounded-lg bg-gray-50 p-3 dark:bg-gray-900/60">
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">{{ c.usuario }}</p>
                        <p class="text-[11px] text-gray-500 dark:text-gray-400" :title="formatarDataHora(c.data_criacao)">{{ tempoRelativo(c.data_criacao) }}</p>
                      </div>
                      <button
                        v-if="c.pode_excluir"
                        type="button"
                        class="rounded p-1 text-gray-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                        :disabled="excluindoComentario === c.id"
                        aria-label="Excluir comentário"
                        @click="excluirComentario(c)"
                      >
                        <Trash2 class="h-4 w-4" />
                      </button>
                    </div>
                    <p class="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{{ c.comentario }}</p>
                  </article>
                </div>

                <form class="mt-4 space-y-2 border-t border-gray-100 pt-4 dark:border-gray-700" @submit.prevent="enviarComentario">
                  <textarea
                    v-model="novoComentario"
                    rows="3"
                    class="input"
                    placeholder="Escreva um comentário… (Ctrl+Enter para enviar)"
                    :disabled="enviandoComentario"
                    @keydown.ctrl.enter.prevent="enviarComentario"
                    @keydown.meta.enter.prevent="enviarComentario"
                  ></textarea>
                  <div class="flex items-center justify-between gap-2">
                    <p v-if="erroComentario" class="text-xs text-red-600">{{ erroComentario }}</p>
                    <button type="submit" class="btn-primary ml-auto" :disabled="enviandoComentario || !novoComentario.trim()">
                      <Loader2 v-if="enviandoComentario" class="h-4 w-4 animate-spin" />
                      <Send v-else class="h-4 w-4" />
                      Enviar
                    </button>
                  </div>
                </form>
              </aside>
            </div>
          </template>
        </div>
      </div>

      <!-- ====== MODAL GENÉRICO ====== -->
      <Teleport to="body">
        <Transition name="fade">
          <div
            v-if="modal.tipo"
            class="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            :aria-label="modal.titulo"
            @click.self="fecharModal"
            @keydown.esc="fecharModal"
          >
            <form
              class="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl animate-fade-in-up dark:bg-gray-800 sm:max-w-lg sm:rounded-2xl"
              novalidate
              @submit.prevent="confirmarModal"
            >
              <header class="flex items-center justify-between gap-3 px-4 py-3 text-white sm:px-6" :class="modal.tipo === 'excluir' ? 'bg-gradient-to-r from-red-600 to-red-700' : 'bg-gradient-to-r from-blue-600 to-blue-700'">
                <h3 class="text-base font-bold sm:text-lg">{{ modal.titulo }}</h3>
                <button type="button" class="rounded-full p-1.5 transition hover:bg-white/20" aria-label="Fechar" @click="fecharModal">
                  <X class="h-5 w-5" />
                </button>
              </header>

              <div class="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
                <!-- Confirmações simples -->
                <p v-if="modal.tipo === 'iniciar' || modal.tipo === 'reabrir' || modal.tipo === 'excluir'" class="text-sm text-gray-700 dark:text-gray-300">
                  {{ modal.mensagem }}
                </p>

                <!-- Finalizar -->
                <template v-if="modal.tipo === 'finalizar'">
                  <fieldset>
                    <legend class="mb-2 text-sm font-medium text-gray-800 dark:text-gray-100">Como o equipamento ficou? <span class="text-red-500">*</span></legend>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <label
                        v-for="op in OPCOES_STATUS_APOS"
                        :key="op.valor"
                        class="flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition"
                        :class="formModal.status_apos_evento === op.valor
                          ? (op.valor === 'parado' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20')
                          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800'"
                      >
                        <input v-model="formModal.status_apos_evento" type="radio" name="status_apos" :value="op.valor" class="mt-0.5" />
                        <span>
                          <span class="block font-medium text-gray-900 dark:text-white">{{ op.label }}</span>
                          <span class="block text-xs text-gray-500 dark:text-gray-400">{{ op.descricao }}</span>
                        </span>
                      </label>
                    </div>
                    <p v-if="errosModal.status_apos_evento" class="mt-1 text-xs text-red-600">{{ errosModal.status_apos_evento }}</p>
                    <p v-if="!evento?.equipamento" class="mt-2 text-xs text-gray-500 dark:text-gray-400">Este evento não tem equipamento vinculado; a escolha fica só no registro.</p>
                  </fieldset>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Solução aplicada</span>
                    <select v-model="formModal.solucao_evento" class="input">
                      <option value="">Não informada</option>
                      <option v-for="s in solucoes" :key="s.id" :value="s.id">{{ s.nome }}</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Data de conclusão</span>
                    <input v-model="formModal.data_final" type="date" class="input" :min="evento?.data_inicio || undefined" :max="hojeISO()" />
                    <p v-if="errosModal.data_final" class="mt-1 text-xs text-red-600">{{ errosModal.data_final }}</p>
                  </label>
                </template>

                <!-- Orçamento -->
                <template v-if="modal.tipo === 'orcamento'">
                  <p class="text-sm text-gray-600 dark:text-gray-300">
                    {{ evento?.status === 'finalizado'
                      ? 'O evento está finalizado; o orçamento pode ser ajustado sem reabri-lo (ex.: nota fiscal recebida depois).'
                      : 'Valor previsto ou já aprovado para este atendimento.' }}
                  </p>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Orçamento</span>
                    <input ref="inputOrcamento" :value="orcamentoFormatado" type="text" inputmode="numeric" class="input text-lg font-semibold" placeholder="R$ 0,00" @input="aoDigitarOrcamento" />
                    <span class="mt-1 block text-[11px] text-gray-500 dark:text-gray-400">
                      Atual: {{ evento?.orcamento ? formatarMoeda(evento.orcamento) : 'não informado' }} · deixe em branco para remover.
                    </span>
                  </label>
                </template>

                <!-- Editar -->
                <template v-if="modal.tipo === 'editar'">
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Descrição <span class="text-red-500">*</span></span>
                    <textarea v-model="formModal.descricao" rows="3" class="input" :class="{ 'border-red-500': errosModal.descricao }"></textarea>
                    <p v-if="errosModal.descricao" class="mt-1 text-xs text-red-600">{{ errosModal.descricao }}</p>
                  </label>
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label class="block">
                      <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Motivo</span>
                      <select v-model="formModal.motivo_evento" class="input">
                        <option value="">Não informado</option>
                        <option v-for="m in motivos" :key="m.id" :value="m.id">{{ m.nome }}</option>
                      </select>
                    </label>
                    <label class="block">
                      <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Solução</span>
                      <select v-model="formModal.solucao_evento" class="input">
                        <option value="">Ainda não definida</option>
                        <option v-for="s in solucoes" :key="s.id" :value="s.id">{{ s.nome }}</option>
                      </select>
                    </label>
                    <label class="block">
                      <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Data de início</span>
                      <input v-model="formModal.data_inicio" type="date" class="input" :class="{ 'border-red-500': errosModal.data_inicio }" :min="evento?.status === 'pendente' ? amanhaISO : undefined" />
                      <p v-if="evento?.status === 'pendente'" class="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Agendado: precisa ser uma data futura.</p>
                      <p v-if="errosModal.data_inicio" class="mt-1 text-xs text-red-600">{{ errosModal.data_inicio }}</p>
                    </label>
                    <div class="block">
                      <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Previsão de término</span>
                      <input v-model="formModal.data_final" type="date" class="input" :class="{ 'border-red-500': errosModal.data_final }" :disabled="formModal.sem_previsao" :min="formModal.data_inicio || undefined" />
                      <label v-if="evento?.categoria_evento !== 'preventiva'" class="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <input v-model="formModal.sem_previsao" type="checkbox" class="h-4 w-4 rounded" /> Sem previsão
                      </label>
                      <p v-if="errosModal.data_final" class="mt-1 text-xs text-red-600">{{ errosModal.data_final }}</p>
                    </div>
                    <label class="block">
                      <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Orçamento</span>
                      <input :value="orcamentoFormatado" type="text" inputmode="numeric" class="input" placeholder="R$ 0,00" @input="aoDigitarOrcamento" />
                    </label>
                    <label
                      v-if="evento?.equipamento"
                      class="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition"
                      :class="formModal.is_parada ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'"
                    >
                      <input v-model="formModal.is_parada" type="checkbox" class="mt-0.5 h-4 w-4 rounded" />
                      <span class="text-sm">
                        <span class="block font-medium text-gray-900 dark:text-white">Equipamento parado</span>
                        <span class="block text-xs text-gray-500 dark:text-gray-400">Sem exames enquanto o evento estiver em atendimento.</span>
                      </span>
                    </label>
                  </div>
                </template>

                <p v-if="erroModal" class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">{{ erroModal }}</p>
              </div>

              <footer class="flex justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-800 sm:px-6">
                <button type="button" class="btn-secondary" :disabled="acaoEmAndamento" @click="fecharModal">Cancelar</button>
                <button type="submit" :class="modal.tipo === 'excluir' ? 'btn-danger' : 'btn-primary'" :disabled="acaoEmAndamento">
                  <Loader2 v-if="acaoEmAndamento" class="h-4 w-4 animate-spin" />
                  {{ modal.confirmar }}
                </button>
              </footer>
            </form>
          </div>
        </Transition>
      </Teleport>

      <!-- ====== TOAST ====== -->
      <Teleport to="body">
        <Transition name="slide-down">
          <div
            v-if="toast"
            class="fixed left-1/2 top-4 z-[110] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg"
            :class="toast.tipo === 'erro' ? 'bg-red-600' : 'bg-emerald-600'"
            role="status"
          >
            {{ toast.mensagem }}
          </div>
        </Transition>
      </Teleport>
    </template>
  </BaseTemplate>
</template>

<script setup>
import BaseTemplate from '@/views/templates/BaseTemplate.vue';
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ArrowLeft, RefreshCw, XCircle, X, Play, CheckCircle2, Pencil, RotateCcw, Trash2,
  MessageSquare, Send, Loader2, FileText, CalendarClock, Wrench, Flag, Wallet,
} from 'lucide-vue-next';
import apiClient from '@/services/authService';
import { useUserStore } from '@/stores/userStore';
import {
  OPCOES_STATUS_APOS, statusMeta, categoriaMeta, equipamentoMeta, podeGerirEventos,
  formatarData, formatarDataHora, formatarMoeda, diasEmAberto, tempoRelativo, hojeISO, mensagemErro,
} from './eventos';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const eventoId = computed(() => parseInt(route.params.id, 10));
const evento = ref(null);
const comentarios = ref([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const erro = ref('');

const podeGerir = computed(() => evento.value?.pode_gerir ?? podeGerirEventos(userStore.grupos || []));

const amanhaISO = computed(() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
});

// ---------------------------------------------------------------------------
// Carregamento
// ---------------------------------------------------------------------------
async function carregar() {
  if (!eventoId.value) {
    router.replace({ name: 'EventosList' });
    return;
  }
  isLoading.value = true;
  erro.value = '';
  try {
    const { data } = await apiClient.get(`/api/v1/events/${eventoId.value}/detalhes/`);
    evento.value = data;
    comentarios.value = data.comentarios || [];
  } catch (error) {
    erro.value = error?.response?.status === 404 ? 'Evento não encontrado.' : mensagemErro(error, 'Não foi possível carregar o evento.');
    console.error('Erro ao carregar o evento:', error);
  } finally {
    isLoading.value = false;
  }
}

async function recarregar() {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    const { data } = await apiClient.get(`/api/v1/events/${eventoId.value}/detalhes/`);
    evento.value = data;
    comentarios.value = data.comentarios || [];
  } catch (error) {
    mostrarToast(mensagemErro(error, 'Falha ao recarregar.'), 'erro');
  } finally {
    isRefreshing.value = false;
  }
}

const voltar = () => (window.history.length > 1 ? router.back() : router.push({ name: 'EventosList' }));

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------
const resumoTempo = computed(() => {
  const e = evento.value;
  if (!e) return '';
  const dias = diasEmAberto(e);
  if (e.status === 'finalizado') return `Concluído em ${formatarDataHora(e.data_fechamento || e.data_final)}${dias !== null ? ` · ${dias} dia(s) de atendimento` : ''}`;
  if (e.status === 'pendente') return `Agendado para ${formatarData(e.data_inicio)}`;
  return `Em atendimento há ${dias ?? 0} dia(s)`;
});

const informacoes = computed(() => {
  const e = evento.value;
  if (!e) return [];
  const itens = [
    { label: 'Unidade', valor: e.unidade?.nome || '—' },
    { label: 'Entidade', valor: e.entidade?.nome || '—' },
    { label: 'Equipamento', valor: e.equipamento ? `${e.equipamento.nome}${e.equipamento.modelo ? ` (${e.equipamento.modelo})` : ''}` : 'Não vinculado' },
    { label: 'Status atual do equipamento', valor: e.equipamento ? equipamentoMeta(e.equipamento.status).label : '—', badge: e.equipamento ? equipamentoMeta(e.equipamento.status).badge : null },
    { label: 'Categoria', valor: categoriaMeta(e.categoria_evento).longo, badge: categoriaMeta(e.categoria_evento).badge },
    { label: 'Motivo', valor: e.motivo_evento?.nome || 'Não informado' },
    { label: 'Solução', valor: e.solucao_evento?.nome || (e.status === 'finalizado' ? 'Não informada' : 'Ainda não definida') },
    { label: 'Orçamento', valor: e.orcamento ? formatarMoeda(e.orcamento) : 'Não informado' },
    { label: 'Registrado por', valor: e.criado_por?.nome || '—' },
    { label: 'Registrado em', valor: formatarDataHora(e.data_abertura) },
    { label: 'Início', valor: formatarData(e.data_inicio, 'Não informado') },
    { label: e.status === 'finalizado' ? 'Concluído em' : 'Previsão de término', valor: e.status === 'finalizado' ? formatarDataHora(e.data_fechamento || e.data_final) : formatarData(e.data_final, 'Sem previsão') },
  ];
  if (e.equipamento) {
    itens.push(
      { label: 'Equipamento antes do evento', valor: equipamentoMeta(e.status_atual_equipamento).label, badge: e.status_atual_equipamento ? equipamentoMeta(e.status_atual_equipamento).badge : null },
      { label: 'Equipamento durante o evento', valor: e.status_durante_manutencao ? equipamentoMeta(e.status_durante_manutencao).label : (e.status === 'pendente' ? 'Sem alteração até o início' : '—'), badge: e.status_durante_manutencao ? equipamentoMeta(e.status_durante_manutencao).badge : null },
      { label: 'Equipamento após o evento', valor: e.status_apos_evento ? equipamentoMeta(e.status_apos_evento).label : 'Definido ao finalizar', badge: e.status_apos_evento ? equipamentoMeta(e.status_apos_evento).badge : null },
    );
  }
  return itens;
});

const linhaDoTempo = computed(() => {
  const e = evento.value;
  if (!e) return [];
  const iniciado = e.status !== 'pendente';
  const finalizado = e.status === 'finalizado';
  return [
    { label: 'Registro', descricao: `${formatarDataHora(e.data_abertura)}${e.criado_por?.nome ? ` · ${e.criado_por.nome}` : ''}`, concluida: true, cor: 'bg-blue-600', icon: FileText },
    { label: iniciado ? 'Início do atendimento' : 'Início agendado', descricao: formatarData(e.data_inicio, 'Data não informada'), concluida: iniciado, cor: 'bg-amber-500', icon: iniciado ? Wrench : CalendarClock },
    ...(finalizado
      ? [{ label: 'Finalizado', descricao: `${formatarDataHora(e.data_fechamento || e.data_final)}${e.status_apos_evento ? ` · equipamento ${equipamentoMeta(e.status_apos_evento).label.toLowerCase()}` : ''}`, concluida: true, cor: 'bg-emerald-600', icon: Flag }]
      : [{ label: 'Previsão de término', descricao: e.data_final ? `${formatarData(e.data_final)}${e.atrasado ? ' · atrasado' : ''}` : 'Sem previsão definida', concluida: false, cor: 'bg-emerald-600', icon: Flag }]),
  ];
});

// ---------------------------------------------------------------------------
// Ações (modal genérico)
// ---------------------------------------------------------------------------
const modal = reactive({ tipo: '', titulo: '', mensagem: '', confirmar: 'Confirmar' });
const formModal = ref({});
const errosModal = ref({});
const erroModal = ref('');
const acaoEmAndamento = ref(false);
const motivos = ref([]);
const solucoes = ref([]);
const orcamentoFormatado = ref('');

async function carregarOpcoes() {
  if (motivos.value.length || solucoes.value.length) return;
  const entidadeId = evento.value?.entidade?.id;
  try {
    const [m, s] = await Promise.all([
      apiClient.get('/api/v1/events/reasons/', { params: { entidade_id: entidadeId } }),
      apiClient.get('/api/v1/events/solutions/', { params: { entidade_id: entidadeId } }),
    ]);
    motivos.value = m.data;
    solucoes.value = s.data;
  } catch (error) {
    console.error('Erro ao carregar motivos/soluções:', error);
  }
}

function confirmar(tipo) {
  const textos = {
    iniciar: { titulo: 'Iniciar atendimento', mensagem: `O evento passa para "Em atendimento" e o equipamento fica ${evento.value.is_parada ? '"parado"' : '"em atendimento"'} até a finalização.`, confirmar: 'Iniciar' },
    reabrir: { titulo: 'Reabrir evento', mensagem: 'O evento volta para "Em atendimento" e o equipamento é marcado como fora de operação novamente.', confirmar: 'Reabrir' },
    excluir: { titulo: 'Excluir evento', mensagem: `Excluir permanentemente o evento #${evento.value.id} e seus ${comentarios.value.length} comentário(s)? O status do equipamento volta ao que era antes do evento.`, confirmar: 'Excluir' },
  };
  Object.assign(modal, { tipo, ...textos[tipo] });
  erroModal.value = '';
}

async function abrirFinalizar() {
  await carregarOpcoes();
  formModal.value = {
    status_apos_evento: 'em_funcionamento',
    solucao_evento: evento.value.solucao_evento?.id || '',
    data_final: hojeISO(),
  };
  errosModal.value = {};
  erroModal.value = '';
  Object.assign(modal, { tipo: 'finalizar', titulo: 'Finalizar evento', mensagem: '', confirmar: 'Finalizar' });
}

const inputOrcamento = ref(null);

async function abrirOrcamento() {
  const e = evento.value;
  formModal.value = { orcamento: e.orcamento ?? null };
  orcamentoFormatado.value = e.orcamento ? formatarMoeda(e.orcamento) : '';
  errosModal.value = {};
  erroModal.value = '';
  Object.assign(modal, { tipo: 'orcamento', titulo: e.orcamento ? 'Atualizar orçamento' : 'Informar orçamento', mensagem: '', confirmar: 'Salvar orçamento' });
  await nextTick();
  inputOrcamento.value?.focus();
}

async function abrirEdicao() {
  await carregarOpcoes();
  const e = evento.value;
  formModal.value = {
    descricao: e.descricao,
    motivo_evento: e.motivo_evento?.id || '',
    solucao_evento: e.solucao_evento?.id || '',
    data_inicio: e.data_inicio || '',
    data_final: e.data_final || '',
    sem_previsao: !e.data_final,
    orcamento: e.orcamento ?? null,
    is_parada: !!e.is_parada,
  };
  orcamentoFormatado.value = e.orcamento ? formatarMoeda(e.orcamento) : '';
  errosModal.value = {};
  erroModal.value = '';
  Object.assign(modal, { tipo: 'editar', titulo: `Editar evento #${e.id}`, mensagem: '', confirmar: 'Salvar alterações' });
}

function fecharModal() {
  if (acaoEmAndamento.value) return;
  modal.tipo = '';
}

watch(() => formModal.value.sem_previsao, (sem) => {
  if (sem && modal.tipo === 'editar') formModal.value.data_final = '';
});

function aoDigitarOrcamento(e) {
  const digitos = e.target.value.replace(/\D/g, '');
  if (!digitos) {
    orcamentoFormatado.value = '';
    formModal.value.orcamento = null;
    return;
  }
  const numero = parseInt(digitos, 10) / 100;
  formModal.value.orcamento = numero.toFixed(2);
  orcamentoFormatado.value = formatarMoeda(numero);
}

function validarModal() {
  const f = formModal.value;
  const erros = {};
  if (modal.tipo === 'finalizar') {
    if (!f.status_apos_evento) erros.status_apos_evento = 'Escolha como o equipamento ficou.';
    if (f.data_final && evento.value.data_inicio && f.data_final < evento.value.data_inicio) erros.data_final = 'A conclusão não pode ser anterior ao início.';
  }
  if (modal.tipo === 'editar') {
    if (!f.descricao || !f.descricao.trim()) erros.descricao = 'A descrição é obrigatória.';
    if (evento.value.status === 'pendente' && f.data_inicio && f.data_inicio <= hojeISO()) erros.data_inicio = 'Evento agendado precisa de data futura (ou use "Iniciar agora").';
    if (evento.value.categoria_evento === 'preventiva' && !f.data_final) erros.data_final = 'Preventiva exige previsão de término.';
    if (f.data_inicio && f.data_final && f.data_final < f.data_inicio) erros.data_final = 'A previsão não pode ser anterior ao início.';
  }
  return erros;
}

function payloadModal() {
  const f = formModal.value;
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  switch (modal.tipo) {
    case 'iniciar': return { status: 'em_atendimento' };
    case 'orcamento': return { orcamento: f.orcamento === null || f.orcamento === '' ? null : Number(f.orcamento) };
    case 'reabrir': return { status: 'em_atendimento' };
    case 'finalizar': return {
      status: 'finalizado',
      status_apos_evento: f.status_apos_evento,
      ...(f.solucao_evento ? { solucao_evento: num(f.solucao_evento) } : {}),
      ...(f.data_final ? { data_final: f.data_final } : {}),
    };
    case 'editar': return {
      descricao: f.descricao.trim(),
      motivo_evento: num(f.motivo_evento),
      solucao_evento: num(f.solucao_evento),
      data_inicio: f.data_inicio || null,
      data_final: f.sem_previsao ? null : f.data_final || null,
      orcamento: f.orcamento === null || f.orcamento === '' ? null : Number(f.orcamento),
      is_parada: !!f.is_parada,
    };
    default: return {};
  }
}

const MENSAGENS_SUCESSO = {
  iniciar: 'Atendimento iniciado.',
  reabrir: 'Evento reaberto.',
  finalizar: 'Evento finalizado.',
  editar: 'Alterações salvas.',
  orcamento: 'Orçamento atualizado.',
  excluir: 'Evento excluído.',
};

async function confirmarModal() {
  if (acaoEmAndamento.value) return;
  errosModal.value = validarModal();
  erroModal.value = '';
  if (Object.keys(errosModal.value).length) return;

  acaoEmAndamento.value = true;
  const tipo = modal.tipo;
  try {
    if (tipo === 'excluir') {
      await apiClient.delete(`/api/v1/events/${evento.value.id}/`);
      modal.tipo = '';
      router.replace({ name: 'EventosList' });
      return;
    }
    await apiClient.put(`/api/v1/events/${evento.value.id}/`, payloadModal());
    modal.tipo = '';
    await recarregar();
    mostrarToast(MENSAGENS_SUCESSO[tipo]);
  } catch (error) {
    erroModal.value = mensagemErro(error);
    console.error(`Erro na ação "${tipo}":`, error);
  } finally {
    acaoEmAndamento.value = false;
  }
}

// ---------------------------------------------------------------------------
// Comentários
// ---------------------------------------------------------------------------
const novoComentario = ref('');
const enviandoComentario = ref(false);
const excluindoComentario = ref(null);
const erroComentario = ref('');
const listaComentarios = ref(null);

async function enviarComentario() {
  const texto = novoComentario.value.trim();
  if (!texto || enviandoComentario.value) return;
  enviandoComentario.value = true;
  erroComentario.value = '';
  try {
    const { data } = await apiClient.post(`/api/v1/events/${evento.value.id}/comentarios/`, { comentario: texto });
    comentarios.value.push(data);
    novoComentario.value = '';
    await nextTick();
    if (listaComentarios.value) listaComentarios.value.scrollTop = listaComentarios.value.scrollHeight;
  } catch (error) {
    erroComentario.value = mensagemErro(error, 'Erro ao enviar o comentário.');
  } finally {
    enviandoComentario.value = false;
  }
}

async function excluirComentario(c) {
  if (excluindoComentario.value) return;
  excluindoComentario.value = c.id;
  try {
    await apiClient.delete(`/api/v1/events/comentarios/${c.id}/`);
    comentarios.value = comentarios.value.filter((x) => x.id !== c.id);
  } catch (error) {
    mostrarToast(mensagemErro(error, 'Erro ao excluir o comentário.'), 'erro');
  } finally {
    excluindoComentario.value = null;
  }
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
const toast = ref(null);
let toastTimer = null;
function mostrarToast(mensagem, tipo = 'sucesso') {
  toast.value = { mensagem, tipo };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 3500);
}

watch(eventoId, (novo, antigo) => {
  if (novo && novo !== antigo) carregar();
});

onMounted(carregar);
</script>
